/**
 * Clinician / Admin — AI Recovery Coach proposal review inbox.
 *
 * Loop:
 *  1. Patient submits their daily check-in (`POST /api/checkins`).
 *  2. AI Recovery Coach analyses it, replies to the patient in `/app/coach`
 *     and — if it thinks a small tweak is warranted — creates a
 *     ProposalPending entry (`db.ai_proposals`, status="pending").
 *  3. The assigned clinician sees the red-priority bell and lands here.
 *  4. Options per proposal:
 *       · Accept & send        → publishes AI reply verbatim to patient
 *       · Modify manually      → edits reply then approves
 *       · Ask AI to refine     → sends clinician instructions back to LLM;
 *                                proposal stays pending with a new draft
 *       · Reject               → closes it, no patient message sent
 *  5. On Accept, the patient receives a red-priority `proposal_decided`
 *     bell + a "New plan available" card on their home dashboard.
 */
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { toast } from "sonner";
import { Loader2, Sparkles, User, Check, X, RefreshCw, PenSquare, ChevronDown, ChevronUp, Info } from "lucide-react";

const KIND_TONE = {
  volume:      { color: "#3B82F6", label: "Volume" },
  intensity:   { color: "#F59E0B", label: "Intensity" },
  progression: { color: "#22C55E", label: "Progression" },
  regression:  { color: "#EF4444", label: "Regression" },
};

/** Compact human phrasing of an AI prescription block — matches the
 *  backend `_describe_prescription` output so clinician and patient see
 *  the same summary. */
function formatPrescription(p) {
  if (!p || typeof p !== "object") return "";
  const bits = [];
  if (p.sets != null && p.reps != null) bits.push(`${p.sets}×${p.reps}`);
  else if (p.sets != null) bits.push(`${p.sets} sets`);
  else if (p.reps != null) bits.push(`${p.reps} reps`);
  if (p.weight) bits.push(`@ ${p.weight}`);
  if (p.duration_seconds) bits.push(`${p.duration_seconds}s`);
  if (p.rest_seconds) bits.push(`rest ${p.rest_seconds}s`);
  if (p.tempo) bits.push(`tempo ${p.tempo}`);
  return bits.join(" · ");
}

export default function ClinicianProposals() {
  const [params] = useSearchParams();
  const focusId = params.get("proposal");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending"); // pending | approved | rejected
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({}); // proposal_id -> editable response
  const [mode, setMode] = useState({});     // proposal_id -> "edit" | "refine" | null
  const [refineText, setRefineText] = useState({});
  const [whyOpen, setWhyOpen] = useState({}); // proposal_id -> bool
  const [context, setContext] = useState({}); // proposal_id -> {checkins, comment_hits}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ai/proposals?status=${tab}`);
      const list = r.data || [];
      setItems(list);
      const initial = {};
      list.forEach((p) => { initial[p.id] = p.suggested_response || p.proposed || ""; });
      setDrafts(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load proposals");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`proposal-${focusId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [items, focusId]);

  const approve = async (p, edited) => {
    setBusyId(p.id);
    try {
      const payload = { edited_response: edited ? drafts[p.id] : undefined };
      await api.post(`/ai/proposals/${p.id}/approve`, payload);
      toast.success(edited ? "Approved with your edits — patient notified." : "Approved & sent to patient.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally { setBusyId(null); }
  };

  const reject = async (p) => {
    if (!window.confirm("Reject this AI proposal? The patient will NOT receive a message.")) return;
    setBusyId(p.id);
    try {
      await api.post(`/ai/proposals/${p.id}/reject`, { note: "" });
      toast.success("Proposal rejected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    } finally { setBusyId(null); }
  };

  /* "Why am I seeing this?" — pulls the patient's last 3 check-ins so the
     clinician can see the trend that likely triggered the proposal
     without leaving the page. Silently no-ops on error. */
  const openWhy = async (p) => {
    const isOpen = !!whyOpen[p.id];
    setWhyOpen((s) => ({ ...s, [p.id]: !isOpen }));
    if (context[p.id] || isOpen) return;
    try {
      const r = await api.get(`/checkins/patient/${p.patient_id}`);
      const last3 = (r.data || []).slice(0, 3);
      const hint = (p.exercise_hint || "").toLowerCase();
      const hits = hint
        ? last3.filter((c) => (c.comments || "").toLowerCase().includes(hint)).length
        : 0;
      setContext((s) => ({ ...s, [p.id]: { checkins: last3, comment_hits: hits } }));
    } catch { /* silent */ }
  };

  const refine = async (p) => {
    const instructions = (refineText[p.id] || "").trim();
    if (!instructions) { toast.error("Add your refinement instructions first."); return; }
    setBusyId(p.id);
    try {
      const r = await api.post(`/ai/proposals/${p.id}/refine`, { instructions });
      const refreshed = r.data || {};
      setDrafts((d) => ({ ...d, [p.id]: refreshed.suggested_response || d[p.id] }));
      setMode((m) => ({ ...m, [p.id]: "edit" }));
      setRefineText((t) => ({ ...t, [p.id]: "" }));
      toast.success("AI re-drafted — review the new version below.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI could not refine — try re-phrasing.");
    } finally { setBusyId(null); }
  };

  return (
    <DashLayout role="clinician" title="AI Proposals">
      <div className="max-w-5xl mx-auto px-6 py-6" data-testid="proposals-page">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles size={20} className="text-[#f47a20]" />
          <h1 className="fl-heading text-2xl">AI Recovery Coach proposals</h1>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--fl-muted)" }}>
          When a patient's daily check-in suggests an adjustment, the AI drafts one here for your review — accept it as-is, edit it, ask the AI to try again, or reject it.
        </p>

        <div className="flex gap-2 mb-5">
          {["pending", "approved", "rejected"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${tab === k ? "bg-[#f47a20] text-black" : "fl-btn-ghost"}`}
              data-testid={`proposals-tab-${k}`}
            >
              {k}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="fl-card p-10 text-center" style={{ color: "var(--fl-muted)" }}>
            <Loader2 size={22} className="animate-spin mx-auto mb-3 text-[#f47a20]" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted)" }} data-testid="proposals-empty">
            No {tab} proposals right now.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((p) => {
              const tone = KIND_TONE[p.kind] || { color: "#f47a20", label: p.kind };
              const isBusy = busyId === p.id;
              const m = mode[p.id];
              return (
                <div
                  key={p.id}
                  id={`proposal-${p.id}`}
                  className={`fl-card fl-card-elevated p-5 md:p-6 ${focusId === p.id ? "ring-1 ring-[#f47a20]" : ""}`}
                  data-testid={`proposal-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold tracking-[0.2em] px-2 py-0.5 rounded-full uppercase" style={{ color: tone.color, background: `color-mix(in oklab, ${tone.color} 14%, transparent)`, border: `1px solid color-mix(in oklab, ${tone.color} 30%, transparent)` }}>
                          {tone.label}
                        </span>
                        {p.refined_count > 0 && (
                          <span className="text-[10px] tracking-wider uppercase" style={{ color: "var(--fl-muted-2)" }}>
                            Refined ×{p.refined_count}
                          </span>
                        )}
                        {p.status !== "pending" && (
                          <span className="text-[10px] tracking-wider uppercase" style={{ color: "var(--fl-muted-2)" }}>
                            · {p.status}
                          </span>
                        )}
                      </div>
                      <div className="fl-heading text-lg mt-2 flex items-center gap-2">
                        <User size={14} className="text-[#f47a20]" />
                        {p.patient_name || "Patient"}
                      </div>
                      {p.exercise_hint && (
                        <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>{p.exercise_hint}</div>
                      )}
                    </div>
                    <div className="text-[11px] shrink-0" style={{ color: "var(--fl-muted-2)" }}>
                      {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                    </div>
                  </div>

                  <div className="mt-4 grid md:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}>
                      <div className="fl-label mb-1">Current</div>
                      <div style={{ color: "var(--fl-text-2)" }}>{p.current || "—"}</div>
                      {p.current_prescription && (
                        <div className="text-xs mt-2 font-mono" style={{ color: "var(--fl-muted-2)" }}
                             data-testid={`proposal-current-prescription-${p.id}`}>
                          {formatPrescription(p.current_prescription)}
                        </div>
                      )}
                    </div>
                    <div className="p-3 rounded-md" style={{ background: "color-mix(in oklab, #f47a20 8%, transparent)", border: "1px solid color-mix(in oklab, #f47a20 30%, transparent)" }}>
                      <div className="fl-label mb-1 text-[#f47a20]">AI's proposal</div>
                      <div style={{ color: "var(--fl-text)" }}>{p.proposed || "—"}</div>
                      {p.proposed_prescription && (
                        <div className="text-xs mt-2 font-mono text-[#f47a20]"
                             data-testid={`proposal-proposed-prescription-${p.id}`}>
                          {formatPrescription(p.proposed_prescription)}
                        </div>
                      )}
                    </div>
                  </div>

                  {p.programme_exercise_id && p.proposed_prescription && (
                    <div className="mt-3 text-[11px] flex items-center gap-2 px-3 py-2 rounded-md"
                         style={{
                           background: "color-mix(in oklab, #22C55E 10%, transparent)",
                           border: "1px solid color-mix(in oklab, #22C55E 30%, transparent)",
                           color: "#86EFAC",
                         }}
                         data-testid={`proposal-will-mutate-${p.id}`}>
                      <Check size={12} />
                      <span>
                        <b>Accept</b> will update <b>{p.exercise_name || p.exercise_hint || "this exercise"}</b> on the patient's active programme and snapshot the prior version for audit.
                      </span>
                    </div>
                  )}

                  {p.reasoning && (
                    <div className="mt-3 text-xs italic" style={{ color: "var(--fl-muted)" }}>
                      <b>Reasoning:</b> {p.reasoning}
                    </div>
                  )}

                  {/* Why am I seeing this? — expandable evidence trail */}
                  {p.status === "pending" && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => openWhy(p)}
                        className="text-[11px] inline-flex items-center gap-1 hover:brightness-110"
                        style={{ color: "var(--fl-orange)" }}
                        data-testid={`proposal-why-toggle-${p.id}`}>
                        <Info size={11} />
                        Why am I seeing this?
                        {whyOpen[p.id] ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                      </button>
                      {whyOpen[p.id] && (
                        <div className="mt-2 p-3 rounded-md text-xs space-y-2"
                             style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}
                             data-testid={`proposal-why-${p.id}`}>
                          {p.evidence && (
                            <div>
                              <span className="font-bold" style={{ color: "var(--fl-text)" }}>Evidence: </span>{p.evidence}
                            </div>
                          )}
                          {context[p.id]?.checkins?.length ? (
                            <div>
                              <div className="font-bold mb-1" style={{ color: "var(--fl-text)" }}>Recent check-ins (newest first)</div>
                              <ul className="space-y-1">
                                {context[p.id].checkins.map((c) => (
                                  <li key={c.id} className="font-mono">
                                    {new Date(c.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} — pain {c.pain}/10 · adherence {c.adherence ?? c.compliance_pct ?? "—"}%
                                    {c.comments && <span className="italic"> · "{c.comments.slice(0, 90)}"</span>}
                                  </li>
                                ))}
                              </ul>
                              {context[p.id].comment_hits > 0 && (
                                <div className="mt-1" style={{ color: "var(--fl-orange)" }}>
                                  "{p.exercise_hint}" mentioned in {context[p.id].comment_hits} of the last {context[p.id].checkins.length} check-ins.
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="italic">Loading recent check-ins…</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-5">
                    <div className="fl-label mb-2">Message to patient</div>
                    {m === "edit" ? (
                      <textarea
                        className="fl-input min-h-[110px] resize-y w-full"
                        value={drafts[p.id] || ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                        data-testid={`proposal-edit-${p.id}`}
                      />
                    ) : m === "refine" ? (
                      <div className="space-y-2">
                        <div className="text-xs italic p-3 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", color: "var(--fl-muted)" }}>
                          {drafts[p.id] || p.suggested_response || "—"}
                        </div>
                        <textarea
                          className="fl-input min-h-[80px] resize-y w-full"
                          placeholder="Tell the AI how to change it — e.g. 'Keep the tone warm but drop reps to 8, and mention the ice pack.'"
                          value={refineText[p.id] || ""}
                          onChange={(e) => setRefineText((t) => ({ ...t, [p.id]: e.target.value }))}
                          data-testid={`proposal-refine-input-${p.id}`}
                        />
                      </div>
                    ) : (
                      <div className="text-sm p-3 rounded-md leading-relaxed" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)", color: "var(--fl-text)" }} data-testid={`proposal-suggested-${p.id}`}>
                        {p.suggested_response || "—"}
                      </div>
                    )}
                  </div>

                  {p.status === "pending" && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {m === "refine" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => refine(p)}
                            disabled={isBusy}
                            className="fl-btn text-sm"
                            data-testid={`proposal-refine-submit-${p.id}`}
                          >
                            {isBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            Ask AI to refine
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode((mm) => ({ ...mm, [p.id]: null }))}
                            className="fl-btn-ghost text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : m === "edit" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => approve(p, true)}
                            disabled={isBusy}
                            className="fl-btn text-sm"
                            data-testid={`proposal-approve-edited-${p.id}`}
                          >
                            {isBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                            Send my edited reply
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode((mm) => ({ ...mm, [p.id]: null }))}
                            className="fl-btn-ghost text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => approve(p, false)}
                            disabled={isBusy}
                            className="fl-btn text-sm"
                            data-testid={`proposal-accept-${p.id}`}
                          >
                            {isBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                            Accept &amp; send
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode((mm) => ({ ...mm, [p.id]: "edit" }))}
                            className="fl-btn-ghost text-sm"
                            data-testid={`proposal-modify-${p.id}`}
                          >
                            <PenSquare size={13} /> Modify manually
                          </button>
                          <button
                            type="button"
                            onClick={() => setMode((mm) => ({ ...mm, [p.id]: "refine" }))}
                            className="fl-btn-ghost text-sm"
                            data-testid={`proposal-ask-refine-${p.id}`}
                          >
                            <Sparkles size={13} /> Ask AI to refine
                          </button>
                          <button
                            type="button"
                            onClick={() => reject(p)}
                            disabled={isBusy}
                            className="fl-btn-ghost text-sm text-red-400 hover:text-red-300"
                            data-testid={`proposal-reject-${p.id}`}
                          >
                            <X size={13} /> Reject
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashLayout>
  );
}
